import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { FC } from 'react';
import { useParams } from 'react-router-dom';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { enumKeyByValue, instrumentOwnersSortFn } from '@app/shared/helpers';
import { InstrumentType, OwnerType } from '@app/shared/models/contracts/enums/shared-enums';
import classNames from 'classnames';
import {
  getDealThesisInputCaseForecastDate,
  getDealThesisInputCaseForecastDateShareholderDebtTotalByType,
  getDealThesisInputCaseHistoricEventsDate,
  getDealThesisInputCaseHistoricEventsDateShareholderDebtTotalByType,
  getDealThesisInputCaseValuationDate,
  getDealThesisInputCaseValuationDateShareholderDebtTotalByType,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import { useLocale } from '@app/core/hooks/useLocale';
import { DealThesisSpacerRow2 } from '../DealThesisSpacerRow2';
import DealThesisEmptyHistoricDataColumns from '../../deal-thesis-utils/DealThesisEmptyHistoricDataColumns';
import useShareholderDebtSectionCheck from '../hooks/useShareholderDebtSectionChecks';
import useDealThesisData from '../../deal-thesis-utils/useDealThesisData';

const SlnAndPrefSharesForecastSummarySection2: FC = (): JSX.Element => {
  const { l } = useLocale();
  const { caseId } = useParams();
  const showHistoricDataColumns = useAppSelector(
    (state) => state.uiValues.userSelections.dealThesis.showHistoricDataColumns
  );
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedInputs);
  const { showSlnSection, showPrefSharesSection, showShareholderDebtSection } =
    useShareholderDebtSectionCheck();

  const { filteredHistoricEventData, pwermCaseDataForecasts, getMaxColspan } =
    useDealThesisData(caseId);

  const renderSummarySection = (instrumentType: InstrumentType): JSX.Element => {
    const instrumentTypeKey = enumKeyByValue(InstrumentType, instrumentType);
    return (
      <>
        <tr className="table-primary__row--header">
          <th className="table-primary__cell--section-start-separator table-primary__cell--header-secondary">
            {instrumentType}
          </th>
          <td
            className="table-primary__cell--section-start-separator table-primary__cell--header-secondary"
            colSpan={getMaxColspan() - 1}
          />
        </tr>
        {Object.keys(
          results.cases[caseId!].valuationDate.shareholderDebtSummary[instrumentTypeKey] || {}
        )
          .sort((a, b) =>
            instrumentOwnersSortFn(a as keyof typeof OwnerType, b as keyof typeof OwnerType)
          )
          .map((ownerType, index) => {
            return (
              <tr key={ownerType}>
                <th
                  className={classNames('table-primary__cell--vertical-separator', {
                    'table-primary__cell--section-start-separator': index === 0,
                  })}>
                  {OwnerType[ownerType as keyof typeof OwnerType]} {l('_Total')}
                </th>
                <DealThesisEmptyHistoricDataColumns
                  isHistoricEventsSection
                  isStartSeparator={index === 0}
                />
                {showHistoricDataColumns &&
                  filteredHistoricEventData.map((historicEventYear) => (
                    <td
                      key={historicEventYear.historicEventsYear}
                      className={classNames({
                        'table-primary__cell--section-start-separator': index === 0,
                      })}>
                      <CellValue
                        {...amountRules}
                        value={getDealThesisInputCaseHistoricEventsDate(
                          results,
                          caseId!,
                          historicEventYear.historicEventsYear,
                          (historicForecast) =>
                            historicForecast.shareholderDebtSummary[instrumentTypeKey]?.[ownerType]
                        )}
                      />
                    </td>
                  ))}
                <td
                  className={classNames({
                    'table-primary__cell--section-start-separator': index === 0,
                  })}>
                  <CellValue
                    {...amountRules}
                    value={getDealThesisInputCaseValuationDate(
                      results,
                      caseId!,
                      (caseItem) => caseItem.shareholderDebtSummary[instrumentTypeKey]?.[ownerType]
                    )}
                  />
                </td>
                {pwermCaseDataForecasts?.map((forecast) => (
                  <td
                    key={forecast.forecastId}
                    className={classNames({
                      'table-primary__cell--section-start-separator': index === 0,
                    })}>
                    <CellValue
                      {...amountRules}
                      value={getDealThesisInputCaseForecastDate(
                        results,
                        caseId!,
                        forecast.forecastYear,
                        (forecast) =>
                          forecast?.shareholderDebtSummary[instrumentTypeKey]?.[ownerType]
                      )}
                    />
                  </td>
                ))}
                <td
                  className={classNames('table-primary__cell--vertical-separator', {
                    'table-primary__cell--section-start-separator': index === 0,
                  })}
                />
              </tr>
            );
          })}

        <tr className="table-primary__row--plain">
          <th className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
            <strong>
              {instrumentType} {l('_Total')}
            </strong>
          </th>
          <DealThesisEmptyHistoricDataColumns
            isHistoricEventsSection
            isEndSeparator
            isStartSeparator
          />
          {showHistoricDataColumns &&
            filteredHistoricEventData.map((historicEventYear) => (
              <td
                key={historicEventYear.historicEventsYear}
                className="table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
                <CellValue
                  {...amountRules}
                  value={getDealThesisInputCaseHistoricEventsDateShareholderDebtTotalByType(
                    results,
                    caseId!,
                    historicEventYear.historicEventsYear,
                    instrumentTypeKey
                  )}
                  strong
                />
              </td>
            ))}
          <td className="table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
            <CellValue
              {...amountRules}
              value={getDealThesisInputCaseValuationDateShareholderDebtTotalByType(
                results,
                caseId!,
                instrumentTypeKey
              )}
              strong
            />
          </td>
          {pwermCaseDataForecasts?.map((forecast) => (
            <td
              className="table-primary__cell--section-start-separator table-primary__cell--section-end-separator"
              key={forecast.forecastId + 'sln-totals-sum'}>
              {/* REQUIRES UPDATING AFTER TOTAL IS ADDED TO BACKEND */}
              <CellValue
                {...amountRules}
                value={getDealThesisInputCaseForecastDateShareholderDebtTotalByType(
                  results,
                  caseId!,
                  forecast.forecastYear,
                  instrumentTypeKey
                )}
                strong
              />
            </td>
          ))}
          <td className="table-primary__cell--section-start-separator table-primary__cell--section-end-separator" />
        </tr>
        <DealThesisSpacerRow2 />
      </>
    );
  };

  return (
    <tbody>
      {showShareholderDebtSection && (
        <tr>
          <th className="table-primary__cell--header table-primary__cell--header-alternate">
            {l('_SummaryShareholderDebt')}
          </th>
          <td
            className="table-primary__cell--header table-primary__cell--header-alternate"
            colSpan={getMaxColspan() - 1}
          />
        </tr>
      )}
      {showSlnSection && renderSummarySection(InstrumentType.ShareholderLoanNotes)}
      {showPrefSharesSection && renderSummarySection(InstrumentType.PreferredShares)}
    </tbody>
  );
};

export default SlnAndPrefSharesForecastSummarySection2;
